import { DealsPageLayout } from "../../deals/dealsPageLayout/DealsPageLayout";
import React, { useEffect, useState } from "react";
import * as api from "../../../api/generated-api";
import {
  addClient,
  getLawFirmClients,
  sendClientInvitationReminder,
} from "../../../api/lawfirm/clients";
import { Grid, Typography, useTheme } from "@mui/material";
import { connect } from "react-redux";
import {
  configureAuthHeader,
  configureBaseUrl,
} from "../../../api/axiosConfig";
import { getDeals } from "../../../api/deals/deals";
import { Link } from "react-router-dom";
import { DKChip } from "../../common/DKChip";
import { isEmpty, toValidIdentifier } from "../../../helpers/utilsTS";
import Button from "../../common/forms/button";
import { clickToLinkHandler } from "../../../helpers/utils";
import { CreateOrganizationModal } from "../../organization/CreateOrganizationModal";

type ClientsPageProps = {
  lawFirmOrgId: number;
  token: string;
};

const ClientsPage = ({ lawFirmOrgId, token }: ClientsPageProps) => {
  const [clients, setClients] = useState<api.LawFirmClient[]>([]);
  const [deals, setDeals] = useState<api.Deal[]>([]);
  const [dealsByClient, setDealsByClient] = useState<Map<number, api.Deal[]>>(
    new Map<number, api.Deal[]>()
  );
  const [showNewClientDialog, setShowNewClientDialog] =
    useState<boolean>(false);
  const [newClientData, setNewClientData] =
    useState<api.CompanyWithAdminUserCreate>();
  const [emptyFields, setEmptyFields] = useState<Map<string, boolean>>(
    new Map<string, boolean>()
  );

  configureBaseUrl();
  configureAuthHeader(token);

  const theme = useTheme();

  const fetchAndSaveClients = () => {
    getLawFirmClients(lawFirmOrgId, (fetchedClients) => {
      setClients(fetchedClients);
    });
  };

  const checkAndSet = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const value = event.target.value;
    const updatedEmptyFields = new Map<string, boolean>(emptyFields);

    if (isEmpty(value)) {
      updatedEmptyFields.set(name, true);
    } else {
      updatedEmptyFields.delete(name);
    }

    setEmptyFields(updatedEmptyFields);

    setNewClientData({
      ...newClientData,
      [name]: event.target.value,
    });
  };

  const isNewClientSaveButtonDisabled = () => {
    return (
      isEmpty(newClientData?.name) ||
      isEmpty(newClientData?.email) ||
      isEmpty(newClientData?.firstName) ||
      isEmpty(newClientData?.lastName)
    );
  };

  useEffect(() => {
    fetchAndSaveClients();

    getDeals((fetchedDeals) => {
      setDeals(fetchedDeals);
    });
  }, [lawFirmOrgId]);

  useEffect(() => {
    if (deals && deals.length > 0) {
      const dealsByClientData = new Map<number, api.Deal[]>();

      deals.forEach((deal) => {
        if (dealsByClientData.has(deal.organizationId)) {
          dealsByClientData.set(deal.organizationId, [
            ...dealsByClientData.get(deal.organizationId),
            deal,
          ]);
        } else {
          dealsByClientData.set(deal.organizationId, [deal]);
        }
      });

      setDealsByClient(dealsByClientData);
    }
  }, [deals]);

  return (
    <DealsPageLayout
      title="Clients"
      actionButtons={[
        {
          primary: true,
          label: "New Client",
          dataTestId: "new-client-button",
          onClick: () => {
            setShowNewClientDialog(true);
          },
        },
      ]}
    >
      {!!clients?.length &&
        clients.map((lawFirmClient) => {
          return (
            <>
              <Grid
                data-testid={toValidIdentifier(`law-firm-client-row`)}
                key={lawFirmClient.id}
                container
                sx={{
                  padding: "20px",
                  border: `1px solid ${theme.palette.tertiary.light}`,
                  marginBottom: "20px",
                }}
              >
                <Grid item xs={4}>
                  <Typography variant={"h3"} data-testid="client-name">
                    {lawFirmClient.client.name}
                    {lawFirmClient.status ==
                      api.LawFirmClientStatus.requested && (
                      <DKChip
                        sx={{ marginLeft: "10px" }}
                        label={"Invitation Pending"}
                      />
                    )}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  {dealsByClient.has(lawFirmClient.client.id) &&
                    dealsByClient
                      .get(lawFirmClient.client.id)
                      .sort((a, b) => {
                        // sort deals by date created in ascending order
                        return a.createdAt > b.createdAt ? 1 : -1;
                      })
                      .map((deal) => {
                        return (
                          <DKChip
                            key={deal.id}
                            data-testid={toValidIdentifier(
                              `${lawFirmClient.client.name}-${deal.seriesOfPreferredStockSold}-link`
                            )}
                            sx={{ cursor: "pointer", mr: "8px" }}
                            label={deal.seriesOfPreferredStockSold}
                            component={Link}
                            to={`/${deal.organizationId}/deals/${deal.id}/edit/deal-information`}
                          />
                        );
                      })}
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  {lawFirmClient.status == api.LawFirmClientStatus.accepted && (
                    <Button
                      data-testid={toValidIdentifier(
                        `lawfirm-client-new-deal-button`
                      )}
                      size="medium"
                      onClick={clickToLinkHandler.bind(
                        this,
                        `/${lawFirmClient.client.id}/deals/before-create`
                      )}
                    >
                      New Deal
                    </Button>
                  )}
                  {lawFirmClient.status ==
                    api.LawFirmClientStatus.requested && (
                    <Button
                      data-testid={toValidIdentifier(
                        `lawfirm-client-send-reminder-button`
                      )}
                      size="medium"
                      onClick={() => {
                        sendClientInvitationReminder(
                          lawFirmOrgId,
                          lawFirmClient.id
                        );
                      }}
                    >
                      Send Reminder
                    </Button>
                  )}
                </Grid>
              </Grid>
              <CreateOrganizationModal
                isOpen={showNewClientDialog}
                title={"New Client"}
                contentText={
                  "The CEO will be asked to confirm and activate the company's account."
                }
                dataTestIdModifier={"new-client"}
                isSaveButtonDisabled={isNewClientSaveButtonDisabled()}
                onCancel={() => {
                  setShowNewClientDialog(false);
                }}
                onSave={() => {
                  setShowNewClientDialog(false);
                  addClient(lawFirmOrgId, newClientData, () => {
                    fetchAndSaveClients();
                  });
                }}
                onFieldChange={(event) => {
                  checkAndSet(event);
                }}
                nameLabel={"Client Company Name"}
                emailLabel={"CEO Email"}
                firstNameLabel={"CEO First Name"}
                lastNameLabel={"CEO Last Name"}
                nameErrorLabel={
                  emptyFields.get("name")
                    ? "Client Company Name is required."
                    : undefined
                }
                emailErrorLabel={
                  emptyFields.get("email")
                    ? "CEO Email is required."
                    : undefined
                }
                firstNameErrorLabel={
                  emptyFields.get("firstName")
                    ? "CEO First Name is required."
                    : undefined
                }
                lastNameErrorLabel={
                  emptyFields.get("lastName")
                    ? "CEO Last Name is required."
                    : undefined
                }
              />
            </>
          );
        })}
    </DealsPageLayout>
  );
};

const mapStateToProps = (state, props): ClientsPageProps => {
  const {
    match: {
      params: { lawFirmOrgId },
    },
  } = props;
  const { token } = state.auth;
  return {
    token,
    lawFirmOrgId,
  };
};

export default connect(mapStateToProps)(ClientsPage);
